import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useAdminTranslation } from '../../utils/useAdminTranslation';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  UserX,
  UserCheck,
  Loader2,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  Search,
  X,
  Eye,
  Minus,
  Check,
  Wallet,
  Download,
  Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const USER_ROLES = [
  { value: 'admin', label: { es: 'Administrador', en: 'Administrator' }, color: 'bg-purple-500 hover:bg-purple-600' },
  { value: 'worker', label: { es: 'Trabajador', en: 'Worker' }, color: 'bg-blue-500 hover:bg-blue-600' },
  { value: 'client', label: { es: 'Cliente', en: 'Client' }, color: 'bg-slate-500 hover:bg-slate-600' },
];

export const UsersTab = ({ users, onRefresh, texts }) => {
  const { user, isSuperadmin } = useAuth();
  const { language, at, tx } = useAdminTranslation();
  const userImportInputRef = useRef(null);

  // Local state
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    is_admin: false,
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [importingUsers, setImportingUsers] = useState(false);
  const [showUserImportDialog, setShowUserImportDialog] = useState(false);
  const [userImportResult, setUserImportResult] = useState(null);

  // Wallet adjustment state
  const [walletAdjustDialog, setWalletAdjustDialog] = useState(false);
  const [walletAdjustUser, setWalletAdjustUser] = useState(null);
  const [walletAdjustAmount, setWalletAdjustAmount] = useState('');
  const [walletAdjustReason, setWalletAdjustReason] = useState('');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadPendingUsers = async () => {
    setLoadingPending(true);
    try {
      const response = await axios.get(`${API}/admin/users/pending-approval`);
      setPendingUsers(response.data.users || []);
    } catch (e) { console.error(e); }
    finally { setLoadingPending(false); }
  };

  const approveUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/approve`);
      toast.success(tx('Usuario aprobado', 'User approved'));
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(tx('Error al aprobar', 'Error approving'));
    }
  };

  const rejectUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reject`);
      toast.success(tx('Usuario rechazado', 'User rejected'));
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(tx('Error al rechazar', 'Error rejecting'));
    }
  };

  React.useEffect(() => { loadPendingUsers(); }, []);

  const toggleUserSelection = (userId) => {
    const ns = new Set(selectedUsers);
    if (ns.has(userId)) ns.delete(userId);
    else ns.add(userId);
    setSelectedUsers(ns);
  };

  const isDeletable = (u) => {
    const role = u.is_admin ? 'admin' : (u.role || 'client');
    return role !== 'worker' && role !== 'admin' && role !== 'superadmin';
  };

  const selectAllUsers = () => {
    const deletable = filteredUsers.filter(isDeletable);
    if (selectedUsers.size === deletable.length && deletable.length > 0) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(deletable.map(u => u.id)));
    }
  };

  const bulkDeleteUsers = async () => {
    if (selectedUsers.size === 0) return;
    if (!window.confirm(tx(`¿Eliminar ${selectedUsers.size} usuarios?`, `Delete ${selectedUsers.size} users?`))) return;
    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/admin/users/bulk-delete`, { user_ids: Array.from(selectedUsers) });
      toast.success(response.data.message);
      setSelectedUsers(new Set());
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error al eliminar', 'Error deleting'));
    } finally {
      setBulkDeleting(false);
    }
  };
  const [walletAdjustType, setWalletAdjustType] = useState('add');
  const [adjustingWallet, setAdjustingWallet] = useState(false);

  // Handlers
  const handleUserUpdate = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates);
      toast.success(tx('Usuario actualizado', 'User updated'));
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error actualizando usuario', 'Error updating user'));
    }
  };

  const handleUserDelete = async (userId) => {
    if (!window.confirm(texts.confirmDelete)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(at('users.userDeleted'));
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error eliminando usuario', 'Error deleting user'));
    }
  };

  const handleImpersonateUser = async (userId, userEmail) => {
    if (!window.confirm(tx(
      `¿Quieres suplantar al usuario ${userEmail}? Se abrirá una nueva pestaña con su sesión.`,
      `Do you want to impersonate user ${userEmail}? A new tab will open with their session.`
    ))) return;
    
    try {
      const response = await axios.post(`${API}/admin/impersonate/${userId}`);
      const { access_token, user: impersonatedUser } = response.data;
      
      const impersonationData = {
        token: access_token,
        user: impersonatedUser,
        originalAdmin: user?.email
      };
      
      const newTab = window.open('', '_blank');
      if (newTab) {
        newTab.location.href = `${window.location.origin}/?impersonate=${encodeURIComponent(JSON.stringify(impersonationData))}`;
      }
      
      toast.success(tx(
        `Suplantando a ${userEmail}`,
        `Impersonating ${userEmail}`
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error al suplantar usuario', 'Error impersonating user'));
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error(tx('Por favor completa todos los campos', 'Please fill all fields'));
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users`, userForm);
      toast.success(at('users.userCreated'));
      setUserDialogOpen(false);
      setUserForm({ name: '', email: '', password: '', is_admin: false });
      onRefresh();
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = texts.userCreateError;
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleImportUsers = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingUsers(true);
    setUserImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/users/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUserImportResult(response.data);
      setShowUserImportDialog(true);
      toast.success(tx('Importación completada', 'Import completed'));
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error en la importación', 'Import error'));
    } finally {
      setImportingUsers(false);
      if (userImportInputRef.current) {
        userImportInputRef.current.value = '';
      }
    }
  };

  const handleDownloadUsersTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/template`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'plantilla_usuarios.xlsx';
      link.click();
    } catch (error) {
      toast.error(tx('Error descargando plantilla', 'Error downloading template'));
    }
  };

  const handleWalletAdjustment = async () => {
    if (!walletAdjustUser || !walletAdjustAmount || !walletAdjustReason.trim()) {
      toast.error(tx('Completa todos los campos', 'Fill all fields'));
      return;
    }
    
    const amount = parseFloat(walletAdjustAmount);
    if (amount <= 0) {
      toast.error(tx('El importe debe ser mayor que 0', 'Amount must be greater than 0'));
      return;
    }
    
    setAdjustingWallet(true);
    try {
      await axios.post(`${API}/admin/users/${walletAdjustUser.id}/wallet-adjustment`, {
        amount: amount,
        type: walletAdjustType,
        reason: walletAdjustReason.trim()
      });
      
      const sign = walletAdjustType === 'add' ? '+' : '-';
      toast.success(tx(
        `${sign}€${amount.toFixed(2)} - Notificación enviada al cliente`,
        `${sign}€${amount.toFixed(2)} - Notification sent to customer`
      ));
      
      setWalletAdjustDialog(false);
      setWalletAdjustUser(null);
      setWalletAdjustAmount('');
      setWalletAdjustReason('');
      setWalletAdjustType('add');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Error ajustando saldo', 'Error adjusting wallet'));
    } finally {
      setAdjustingWallet(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = !userSearch || 
      u.email.toLowerCase().includes(userSearch.toLowerCase()) || 
      u.name.toLowerCase().includes(userSearch.toLowerCase());
    const userRole = u.is_admin ? 'admin' : (u.role || 'client');
    const matchesRole = userRoleFilter === 'all' || userRole === userRoleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{texts.users}</CardTitle>
          <div className="flex items-center gap-3">
            {/* Role Filter */}
            <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder={tx('Todos los roles', 'All roles')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tx('Todos los roles', 'All roles')}</SelectItem>
                {USER_ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label[language] || role.label.es}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Email Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={tx('Buscar por email...', 'Search by email...')}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="pl-9 w-[250px]"
                data-testid="users-search-input"
              />
              {userSearch && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => setUserSearch('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            {/* Hidden file input for import */}
            <input
              type="file"
              ref={userImportInputRef}
              accept=".xlsx,.xls"
              onChange={handleImportUsers}
              className="hidden"
            />
            <Button 
              variant="outline" 
              onClick={handleDownloadUsersTemplate}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              {tx('Plantilla', 'Template')}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => userImportInputRef.current?.click()}
              disabled={importingUsers}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              {importingUsers ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {tx('Importar', 'Import')}
            </Button>
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/admin/users/export`, { responseType: 'blob' });
                  const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `clientes_${new Date().toISOString().slice(0,10)}.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                } catch (e) { console.error(e); }
              }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Download className="h-4 w-4 mr-2" />
              {tx('Exportar', 'Export')}
            </Button>
            <Button onClick={() => setUserDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              {texts.addUser}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Pending Approval */}
      {pendingUsers.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/50 mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 text-base flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {tx(`Pendientes de aprobación (${pendingUsers.length})`, `Pending approval (${pendingUsers.length})`)}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableBody>
                {pendingUsers.map(u => (
                  <TableRow key={u.id} className="bg-amber-50/50">
                    <TableCell className="font-medium">{u.name || '-'}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.company || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={() => approveUser(u.id)}>
                          {tx('Aprobar', 'Approve')}
                        </Button>
                        <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => rejectUser(u.id)}>
                          {tx('Rechazar', 'Reject')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Users Table */}
      <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    checked={(() => {
                      const deletable = filteredUsers.filter(isDeletable);
                      return deletable.length > 0 && selectedUsers.size === deletable.length;
                    })()}
                    onCheckedChange={selectAllUsers}
                    className="h-3.5 w-3.5"
                    data-testid="bulk-select-all-checkbox"
                  />
                </TableHead>
                <TableHead>{texts.name}</TableHead>
                <TableHead>{texts.email}</TableHead>
                <TableHead>{texts.role}</TableHead>
                <TableHead>{tx('Saldo', 'Balance')}</TableHead>
                <TableHead className="text-center">Newsletter</TableHead>
                <TableHead>{texts.status}</TableHead>
                <TableHead className="text-center">{tx('Descuento', 'Discount')}</TableHead>
                <TableHead className="text-right">{texts.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((u) => {
                const userRole = u.is_admin ? 'admin' : (u.role || 'client');
                const roleConfig = USER_ROLES.find(r => r.value === userRole) || USER_ROLES[2];
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      {isDeletable(u) ? (
                        <Checkbox
                          checked={selectedUsers.has(u.id)}
                          onCheckedChange={() => toggleUserSelection(u.id)}
                          className="h-3.5 w-3.5"
                          data-testid={`bulk-select-checkbox-${u.id}`}
                        />
                      ) : (
                        <span
                          className="text-[10px] text-muted-foreground"
                          title={tx('Los trabajadores/admins deben eliminarse individualmente', 'Workers/admins must be deleted individually')}
                        >—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Select 
                        value={userRole} 
                        onValueChange={(newRole) => {
                          const updates = { 
                            role: newRole,
                            is_admin: newRole === 'admin'
                          };
                          handleUserUpdate(u.id, updates);
                        }}
                      >
                        <SelectTrigger className="w-[130px] h-8">
                          <Badge className={roleConfig.color}>
                            {roleConfig.label[language] || roleConfig.label.es}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {USER_ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              <Badge className={role.color}>
                                {role.label[language] || role.label.es}
                              </Badge>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {userRole === 'client' ? (
                        <div className="flex items-center gap-1">
                          <span className={`font-semibold ${(u.wallet_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {isSuperadmin ? `€${(u.wallet_balance || 0).toFixed(2)}` : '***'}
                          </span>
                          {isSuperadmin && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                setWalletAdjustUser(u);
                                setWalletAdjustAmount('');
                                setWalletAdjustReason('');
                                setWalletAdjustType('add');
                                setWalletAdjustDialog(true);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => handleUserUpdate(u.id, { newsletter: !u.newsletter })}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                          u.newsletter 
                            ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                        title={u.newsletter ? (tx('Suscrito', 'Subscribed')) : (tx('No suscrito', 'Not subscribed'))}
                      >
                        {u.newsletter ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.is_active ? 'outline' : 'destructive'}>
                        {u.is_active ? texts.active : texts.inactive}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Select 
                        value={String(u.discount_percentage || 0)} 
                        onValueChange={(v) => handleUserUpdate(u.id, { discount_percentage: parseInt(v) })}
                      >
                        <SelectTrigger className="w-[80px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((pct) => (
                            <SelectItem key={pct} value={String(pct)}>
                              {pct}%
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingUser(u);
                            setEditUserDialog(true);
                          }}
                          title={tx('Editar usuario', 'Edit user')}
                          className="text-slate-600 hover:text-slate-800 hover:bg-slate-50"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {u.id !== user?.id && (
                          <>
                            {userRole !== 'admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleImpersonateUser(u.id, u.email)}
                                title={tx('Suplantar usuario', 'Impersonate user')}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUserUpdate(u.id, { is_active: !u.is_active })}
                              title={u.is_active ? texts.deactivate : texts.activate}
                            >
                              {u.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUserDelete(u.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {tx('Editar Usuario', 'Edit User')}
            </DialogTitle>
            <DialogDescription>
              {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              {/* Personal Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{tx('Nombre', 'Name')}</Label>
                  <Input
                    value={editingUser.name || ''}
                    onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>{tx('Email', 'Email')}</Label>
                  <Input
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>{tx('Teléfono', 'Phone')}</Label>
                  <Input
                    value={editingUser.phone || ''}
                    onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                  />
                </div>
                <div>
                  <Label>{tx('NIF/CIF', 'Tax ID')}</Label>
                  <Input
                    value={editingUser.nif_cif || ''}
                    onChange={(e) => setEditingUser({...editingUser, nif_cif: e.target.value})}
                  />
                </div>
              </div>

              {/* Company Info */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">{tx('Datos de Empresa', 'Company Info')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{tx('Empresa', 'Company')}</Label>
                    <Input
                      value={editingUser.company_name || ''}
                      onChange={(e) => setEditingUser({...editingUser, company_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{tx('Dirección', 'Address')}</Label>
                    <Input
                      value={editingUser.address || ''}
                      onChange={(e) => setEditingUser({...editingUser, address: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{tx('Ciudad', 'City')}</Label>
                    <Input
                      value={editingUser.city || ''}
                      onChange={(e) => setEditingUser({...editingUser, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{tx('Código Postal', 'Postal Code')}</Label>
                    <Input
                      value={editingUser.postal_code || ''}
                      onChange={(e) => setEditingUser({...editingUser, postal_code: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{tx('País', 'Country')}</Label>
                    <Input
                      value={editingUser.country || ''}
                      onChange={(e) => setEditingUser({...editingUser, country: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{tx('Provincia', 'Province')}</Label>
                    <Input
                      value={editingUser.province || ''}
                      onChange={(e) => setEditingUser({...editingUser, province: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Account Settings */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">{tx('Configuración de Cuenta', 'Account Settings')}</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>{tx('Rol', 'Role')}</Label>
                    <Select 
                      value={editingUser.role === 'superadmin' ? 'superadmin' : (editingUser.is_admin ? 'admin' : (editingUser.role || 'client'))} 
                      onValueChange={(v) => setEditingUser({...editingUser, role: v, is_admin: v === 'admin' || v === 'worker' || v === 'superadmin'})}
                      disabled={editingUser.role === 'superadmin'}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {editingUser.role === 'superadmin' && (
                          <SelectItem value="superadmin">Superadmin</SelectItem>
                        )}
                        {isSuperadmin && editingUser.role !== 'superadmin' && (
                          <>
                            <SelectItem value="admin">{tx('Administrador', 'Administrator')}</SelectItem>
                            <SelectItem value="worker">{tx('Trabajador', 'Worker')}</SelectItem>
                          </>
                        )}
                        {editingUser.role !== 'superadmin' && (
                          <SelectItem value="client">{tx('Cliente', 'Client')}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {!isSuperadmin && editingUser.role !== 'client' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {tx(
                          'Solo el superadmin puede cambiar roles de admin/trabajador',
                          'Only superadmin can change admin/worker roles'
                        )}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>{tx('Descuento (%)', 'Discount (%)')}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editingUser.discount_percentage || 0}
                      onChange={(e) => setEditingUser({...editingUser, discount_percentage: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>{tx('Saldo', 'Balance')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingUser.wallet_balance || 0}
                      onChange={(e) => setEditingUser({...editingUser, wallet_balance: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingUser.is_active !== false}
                      onCheckedChange={(checked) => setEditingUser({...editingUser, is_active: checked})}
                    />
                    <Label>{tx('Usuario Activo', 'Active User')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingUser.email_verified === true}
                      onCheckedChange={(checked) => setEditingUser({...editingUser, email_verified: checked})}
                    />
                    <Label>{tx('Email Verificado', 'Email Verified')}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingUser.newsletter === true}
                      onCheckedChange={(checked) => setEditingUser({...editingUser, newsletter: checked})}
                    />
                    <Label>{tx('Suscrito a Newsletter', 'Newsletter Subscribed')}</Label>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(false)}>
              {tx('Cancelar', 'Cancel')}
            </Button>
            <Button onClick={() => {
              if (editingUser) {
                handleUserUpdate(editingUser.id, {
                  name: editingUser.name,
                  email: editingUser.email,
                  phone: editingUser.phone,
                  nif_cif: editingUser.nif_cif,
                  company_name: editingUser.company_name,
                  address: editingUser.address,
                  city: editingUser.city,
                  postal_code: editingUser.postal_code,
                  country: editingUser.country,
                  province: editingUser.province,
                  role: editingUser.role,
                  is_admin: editingUser.is_admin,
                  discount_percentage: editingUser.discount_percentage,
                  wallet_balance: editingUser.wallet_balance,
                  is_active: editingUser.is_active,
                  email_verified: editingUser.email_verified,
                });
                setEditUserDialog(false);
                toast.success(tx('Usuario actualizado', 'User updated'));
              }
            }}>
              {tx('Guardar Cambios', 'Save Changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{texts.createUser}</DialogTitle>
            <DialogDescription>
              {tx('Introduce los datos del nuevo usuario', 'Enter the new user details')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{texts.name}</Label>
              <Input
                id="name"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{texts.email}</Label>
              <Input
                id="email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{texts.password}</Label>
              <Input
                id="password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_admin"
                checked={userForm.is_admin}
                onCheckedChange={(checked) => setUserForm({ ...userForm, is_admin: checked })}
              />
              <Label htmlFor="is_admin">{texts.admin}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
              {texts.cancel}
            </Button>
            <Button onClick={handleCreateUser} disabled={creatingUser} className="bg-emerald-600 hover:bg-emerald-700">
              {creatingUser ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {texts.createUser}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Import Results Dialog */}
      <Dialog open={showUserImportDialog} onOpenChange={setShowUserImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              {tx('Importación Completada', 'Import Completed')}
            </DialogTitle>
          </DialogHeader>
          {userImportResult && (
            <div className="space-y-3 py-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-green-700 font-medium">{tx('Usuarios creados', 'Users created')}</span>
                <Badge className="bg-green-600">{userImportResult.created || 0}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-700 font-medium">{tx('Usuarios actualizados', 'Users updated')}</span>
                <Badge className="bg-blue-600">{userImportResult.updated || 0}</Badge>
              </div>
              {userImportResult.skipped > 0 && (
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="text-yellow-700 font-medium">{tx('Usuarios omitidos', 'Users skipped')}</span>
                  <Badge className="bg-yellow-600">{userImportResult.skipped}</Badge>
                </div>
              )}
              {userImportResult.errors && userImportResult.errors.length > 0 && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                  <p className="text-red-700 font-medium mb-2">{tx('Errores:', 'Errors:')}</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {userImportResult.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {userImportResult.errors.length > 5 && (
                      <li>... {tx('y', 'and')} {userImportResult.errors.length - 5} {tx('más', 'more')}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowUserImportDialog(false)}>
              {tx('Cerrar', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wallet Adjustment Dialog */}
      <Dialog open={walletAdjustDialog} onOpenChange={setWalletAdjustDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-green-600" />
              {tx('Ajustar Saldo', 'Adjust Balance')}
            </DialogTitle>
            <DialogDescription>
              {walletAdjustUser && (
                <span>
                  {walletAdjustUser.name} ({walletAdjustUser.email}) - 
                  {tx(' Saldo actual: ', ' Current balance: ')}
                  <strong className="text-green-600">€{(walletAdjustUser.wallet_balance || 0).toFixed(2)}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Type selection */}
            <div className="space-y-2">
              <Label>{tx('Tipo de ajuste', 'Adjustment type')}</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={walletAdjustType === 'add' ? 'default' : 'outline'}
                  className={walletAdjustType === 'add' ? 'flex-1 bg-green-600 hover:bg-green-700' : 'flex-1'}
                  onClick={() => setWalletAdjustType('add')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {tx('Añadir saldo', 'Add balance')}
                </Button>
                <Button
                  type="button"
                  variant={walletAdjustType === 'subtract' ? 'destructive' : 'outline'}
                  className="flex-1"
                  onClick={() => setWalletAdjustType('subtract')}
                >
                  <Minus className="h-4 w-4 mr-2" />
                  {tx('Quitar saldo', 'Subtract balance')}
                </Button>
              </div>
            </div>
            
            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="wallet-amount">{tx('Importe (€)', 'Amount (€)')}</Label>
              <Input
                id="wallet-amount"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={walletAdjustAmount}
                onChange={(e) => setWalletAdjustAmount(e.target.value)}
              />
            </div>
            
            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="wallet-reason">
                {tx('Motivo (se enviará al cliente por email)', 'Reason (will be sent to customer by email)')}
              </Label>
              <textarea
                id="wallet-reason"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={tx('Ej: Compensación por retraso en el pedido #123', 'E.g.: Compensation for delay in order #123')}
                value={walletAdjustReason}
                onChange={(e) => setWalletAdjustReason(e.target.value)}
              />
            </div>
            
            {/* Preview */}
            {walletAdjustAmount && parseFloat(walletAdjustAmount) > 0 && walletAdjustUser && (
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-muted-foreground mb-1">{tx('Nuevo saldo:', 'New balance:')}</p>
                <p className={`text-xl font-bold ${walletAdjustType === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                  €{(walletAdjustType === 'add' 
                    ? (walletAdjustUser.wallet_balance || 0) + parseFloat(walletAdjustAmount)
                    : (walletAdjustUser.wallet_balance || 0) - parseFloat(walletAdjustAmount)
                  ).toFixed(2)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWalletAdjustDialog(false)}>
              {tx('Cancelar', 'Cancel')}
            </Button>
            <Button 
              onClick={handleWalletAdjustment}
              disabled={adjustingWallet || !walletAdjustAmount || !walletAdjustReason.trim()}
              className={walletAdjustType === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {adjustingWallet ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {tx('Procesando...', 'Processing...')}
                </>
              ) : (
                <>
                  {walletAdjustType === 'add' ? <Plus className="h-4 w-4 mr-2" /> : <Minus className="h-4 w-4 mr-2" />}
                  {tx('Confirmar y Notificar', 'Confirm & Notify')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating bar for bulk actions */}
      {selectedUsers.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg p-3 flex items-center justify-center gap-4">
          <span className="text-sm font-medium">{selectedUsers.size} {tx('usuarios seleccionados', 'users selected')}</span>
          <Button
            onClick={bulkDeleteUsers}
            disabled={bulkDeleting}
            variant="destructive"
            size="sm"
          >
            {bulkDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            {tx(`Eliminar ${selectedUsers.size}`, `Delete ${selectedUsers.size}`)}
          </Button>
          <Button onClick={() => setSelectedUsers(new Set())} variant="outline" size="sm">
            {tx('Limpiar', 'Clear')}
          </Button>
        </div>
      )}
    </>
  );
};

export default UsersTab;
